import React, { useEffect, useState } from 'react';

/**
 * AnimatedBeam component that draws an animated SVG path between two elements
 * @param {Object} props
 * @param {React.RefObject} props.containerRef - Ref to the container element
 * @param {React.RefObject} props.fromRef - Ref to the source element
 * @param {React.RefObject} props.toRef - Ref to the target element
 * @param {number} props.curvature - Curve amount (positive = curve up, negative = curve down)
 * @param {number} props.duration - Animation duration in seconds
 * @param {string} props.fromSide - 'right' for large screens, 'bottom' for small screens
 */
export function AnimatedBeam({
    containerRef,
    fromRef,
    toRef,
    curvature = 60,
    duration = 4,
    fromSide = 'right'
}) {
    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        function update() {
            if (!containerRef.current || !fromRef.current || !toRef.current) return;

            // Check if mobile
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);

            const containerRect = containerRef.current.getBoundingClientRect();
            const fromRect = fromRef.current.getBoundingClientRect();
            const toRect = toRef.current.getBoundingClientRect();

            let s, e;

            if (mobile) {
                // On mobile: from bottom center of input, to top center of output
                s = {
                    x: fromRect.left + fromRect.width / 2 - containerRect.left,
                    y: fromRect.bottom - containerRect.top,
                };
                e = {
                    x: toRect.left + toRect.width / 2 - containerRect.left,
                    y: toRect.top - containerRect.top,
                };
            } else {
                // On desktop: from right side center of input, to left side center of output
                s = {
                    x: fromRect.right - containerRect.left,
                    y: fromRect.top + fromRect.height / 2 - containerRect.top,
                };
                e = {
                    x: toRect.left - containerRect.left,
                    y: toRect.top + toRect.height / 2 - containerRect.top,
                };
            }

            setStart(s);
            setEnd(e);
        }

        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [containerRef, fromRef, toRef]);

    if (!start || !end) return null;

    // Adjust curvature based on screen size
    const adjustedCurvature = isMobile ? -Math.abs(curvature) : curvature;
    
    // Check if path would be horizontal (start and end Y are approximately the same)
    const wouldBeHorizontal = Math.abs(start.y - end.y) < 1;
    
    // For horizontal paths, slightly adjust end.y to make gradient work
    const adjustedEndY = wouldBeHorizontal && !isMobile 
        ? end.y + 3  // Slight adjustment for horizontal paths
        : end.y;
    
    const midX = (start.x + end.x) / 2;
    const midY = isMobile 
        ? (start.y + adjustedEndY) / 2 + adjustedCurvature
        : (start.y + adjustedEndY) / 2 - adjustedCurvature;
    
    const d = `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${adjustedEndY}`;
    const beamId = `beam-gradient-${Math.random().toString(36).substring(7)}`;

    // Check if path is horizontal (all Y coordinates are approximately the same)
    const isHorizontal = Math.abs(start.y - adjustedEndY) < 1 && Math.abs(start.y - midY) < 1;

    return (
        <svg
            className="pointer-events-none"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                overflow: 'visible',
                zIndex: 1
            }}
        >
            <defs>
                {isHorizontal ? (
                    // For horizontal paths, use a gradient along the path with userSpaceOnUse
                    <linearGradient 
                        id={beamId} 
                        x1={start.x} 
                        x2={end.x} 
                        y1={start.y} 
                        y2={end.y}
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0%" stopColor="#AB5898" />
                        <stop offset="50%" stopColor="#5C4BD3" />
                        <stop offset="100%" stopColor="#AB5898" />
                    </linearGradient>
                ) : (
                    // For curved paths, use a horizontal gradient along the path
                    <linearGradient id={beamId} x1="0%" x2="100%" y1="0%" y2="0%">
                        <stop offset="0%" stopColor="#AB5898" />
                        <stop offset="50%" stopColor="#5C4BD3" />
                        <stop offset="100%" stopColor="#AB5898" />
                    </linearGradient>
                )}
                {/* Animated gradient mask */}
                <mask id={`beam-mask-${beamId}`}>
                    <path
                        d={d}
                        fill="none"
                        stroke="white"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeDasharray="4 16"
                        style={{
                            // animation: `beam-dash ${duration}s linear infinite`,
                            animation: `beam-dash 6s linear infinite`,
                        }}
                    />
                </mask>
            </defs>
            {/* Faint static path */}
            <path
                d={d}
                fill="none"
                stroke="#888"
                strokeWidth={1.5}
                strokeOpacity={0.25}
            />
            {/* Glowing animated beam */}
            <g mask={`url(#beam-mask-${beamId})`}>
                <path
                    d={d}
                    fill="none"
                    stroke={`url(#${beamId})`}
                    strokeWidth={3}
                    strokeLinecap="round"
                    style={{
                        filter: 'blur(1.5px)',
                    }}
                />
            </g>
        </svg>
    );
}

